import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  input,
  signal,
  untracked,
} from '@angular/core';

const MIN_SCALE = 0.15;
const BOOST = 1.2;
const TICK_MS = 1000;
// Chỉ cập nhật khi volume chênh so với lần trước >= ngưỡng này
const VOLUME_THRESHOLD = 10;

const BAR_CONFIG = [
  { maxScale: 3 / 8, weight: 0.5, jitter: { base: 0.5, spread: 0.8 } },
  { maxScale: 6 / 8, weight: 0.8, jitter: { base: 0.7, spread: 1.0 } },
  { maxScale: 1, weight: 1, jitter: { base: 0.9, spread: 1.2 } },
  { maxScale: 6 / 8, weight: 0.8, jitter: { base: 0.7, spread: 1.0 } },
  { maxScale: 3 / 8, weight: 0.5, jitter: { base: 0.5, spread: 0.8 } },
] as const;

const IDLE_SCALES = BAR_CONFIG.map(() => MIN_SCALE);

// Timer chung cho mọi instance: nhiều icon vẫn chỉ 1 interval
const tick = signal(0);
let subscriberCount = 0;
let intervalId: ReturnType<typeof setInterval> | null = null;

function registerTick(): () => void {
  subscriberCount += 1;
  if (!intervalId) {
    intervalId = setInterval(() => tick.update((n) => n + 1), TICK_MS);
  }
  return () => {
    subscriberCount -= 1;
    if (subscriberCount <= 0 && intervalId) {
      clearInterval(intervalId);
      intervalId = null;
      subscriberCount = 0;
    }
  };
}

@Component({
  selector: 'app-sound-icon',
  standalone: true,
  imports: [],
  templateUrl: './sound-icon.component.html',
  styleUrl: './sound-icon.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SoundIconComponent {
  volume = input<number>(0);
  color = input<string>('white');

  private readonly intensity = computed(() => Math.min(1, Math.max(0, this.volume()) / 100));
  private readonly isActive = computed(() => this.intensity() > 0);

  scales = signal<number[]>([...IDLE_SCALES]);

  private lastVolume = -Infinity;

  constructor() {
    effect(
      (onCleanup) => {
        // Chỉ phụ thuộc trạng thái bật/tắt (đổi khi qua mốc 0),
        // tránh restart timer mỗi lần volume thay đổi giá trị
        if (!this.isActive()) {
          this.lastVolume = -Infinity;
          this.scales.set([...IDLE_SCALES]);
          return;
        }
        onCleanup(registerTick());
      },
      {
        allowSignalWrites: true,
      },
    );

    effect(
      () => {
        tick();

        const volume = untracked(() => Math.min(100, Math.max(0, this.volume())));
        if (volume <= 0) return;

        // Chỉ animate khi volume thay đổi đủ lớn so với lần trước, còn lại giữ nguyên
        if (Math.abs(volume - this.lastVolume) < VOLUME_THRESHOLD) return;
        this.lastVolume = volume;

        const intensity = volume / 100;

        this.scales.set(
          BAR_CONFIG.map((bar) => {
            const jitter = bar.jitter.base + Math.random() * bar.jitter.spread;
            const value = intensity * BOOST * jitter * bar.weight * bar.maxScale;
            return Math.max(MIN_SCALE * bar.maxScale, Math.min(bar.maxScale, value));
          }),
        );
      },
      {
        allowSignalWrites: true,
      },
    );
  }
}
